import React, { useState, useCallback, useRef } from "react";
import { CommonInput, CommonLoader } from "..";
import { FaMagnifyingGlass } from "react-icons/fa6";

const BOOKS_API = "https://www.googleapis.com/books/v1/volumes";

const buildBookDataFromVolume = (volumeInfo) => ({
  publisher: volumeInfo?.publisher,
  publishedDate: volumeInfo?.publishedDate,
  authors: volumeInfo?.authors,
  pageCount: volumeInfo?.pageCount,
  categories: volumeInfo?.categories,
  language: volumeInfo?.language,
  maturityRating: volumeInfo?.maturityRating,
  infoUrl: volumeInfo?.infoLink?.startsWith("http://")
    ? volumeInfo.infoLink.replace("http://", "https://")
    : volumeInfo?.infoLink,
});

/**
 * Google Books search — same endpoint/params pattern as MySifarish SearchBookList (useBookActions).
 */
const CommonBookSearch = ({ onBookSelect, value, onChange }) => {
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const debounceRef = useRef(null);

  const apiKey = process.env.REACT_APP_GOOGLE_API_KEY;

  const fetchSuggestions = useCallback(async (query) => {
    if (!query?.trim()) {
      setSuggestions([]);
      return;
    }
    if (!apiKey) {
      setError("Missing REACT_APP_GOOGLE_API_KEY (enable Books API for this key)");
      setSuggestions([]);
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        q: query.trim(),
        maxResults: "20",
        startIndex: "0",
        key: apiKey,
      });
      const url = `${BOOKS_API}?${params.toString()}`;
      const response = await fetch(url);
      const data = await response.json();

      if (data.items?.length) {
        setSuggestions(data.items);
      } else {
        setSuggestions([]);
        if (data.error?.message) {
          setError(data.error.message);
        } else {
          setError(null);
        }
      }
    } catch (err) {
      setSuggestions([]);
      setError("Something went wrong");
    }
    setIsLoading(false);
  }, [apiKey]);

  const handleInputChange = (val) => {
    onChange(val);
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      fetchSuggestions(val);
    }, 450);
  };

  const handleSelectSuggestion = (book) => {
    const raw = book.volumeInfo || {};
    let thumbnail = raw.imageLinks?.thumbnail;
    if (thumbnail?.startsWith("http://")) {
      thumbnail = thumbnail.replace("http://", "https://");
    }
    const vi = {
      ...raw,
      imageLinks: raw.imageLinks
        ? { ...raw.imageLinks, ...(thumbnail ? { thumbnail } : {}) }
        : raw.imageLinks,
    };
    onChange(vi.title || "");
    setSuggestions([]);
    onBookSelect({
      id: book.id,
      volumeInfo: vi,
      bookData: buildBookDataFromVolume(vi),
    });
  };

  return (
    <div
      className="__movieSearch"
      style={{
        position: "relative",
        ...(error ? { marginBottom: "1rem" } : {}),
      }}
    >
      <CommonInput
        label="Book title"
        placeholder="Search books"
        value={value}
        onChange={(e) => handleInputChange(e.target.value)}
      />
      <button type="button" onClick={() => fetchSuggestions(value)}>
        {isLoading ? (
          <CommonLoader loaderClass="movie_load" />
        ) : (
          <FaMagnifyingGlass />
        )}
      </button>

      {suggestions.length > 0 && (
        <ul
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            background: "#fff",
            border: "1px solid #ccc",
            zIndex: 1000,
            maxHeight: "220px",
            overflowY: "auto",
            listStyle: "none",
            padding: "0.5rem",
          }}
        >
          {suggestions.map((book) => {
            const vi = book.volumeInfo || {};
            const authors = vi.authors?.length
              ? vi.authors.join(", ")
              : "";
            return (
              <li
                key={book.id}
                onClick={() => handleSelectSuggestion(book)}
                style={{
                  padding: "6px 8px",
                  cursor: "pointer",
                  borderBottom: "1px solid #eee",
                }}
              >
                <div style={{ fontWeight: 600 }}>{vi.title}</div>
                {authors && (
                  <div style={{ fontSize: "12px", color: "#555" }}>
                    {authors}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {error && (
        <p
          style={{
            color: "red",
            margin: "0",
            position: "absolute",
            left: "0px",
            bottom: "-20px",
            fontSize: "12px",
          }}
        >
          {error}
        </p>
      )}
    </div>
  );
};

export default CommonBookSearch;
